import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { API, inr, errMsg } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useOutlet } from "../context/OutletContext";

const F = "w-full border border-stone-300 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B29] transition-colors";
const STATES = ["Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal"];
const round2 = (n) => Math.round(n * 100) / 100;

export default function Checkout() {
  const { cart, refresh } = useCart();
  const { user } = useAuth();
  const { outlet } = useOutlet();
  const nav = useNavigate();
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wallet, setWallet] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [f, setF] = useState({ name: user?.name || "", mobile: user?.mobile || "", address: "", city: user?.city || "", state: user?.state || "", pincode: "", payment_method: "COD", notes: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { API.get("/referral").then((r) => setWallet(r.data.balance || 0)).catch(() => {}); }, []);
  const walletCap = cart ? round2(Math.min(wallet, cart.total * 0.10)) : 0;
  const walletUsed = useWallet ? walletCap : 0;
  const payable = cart ? round2(cart.total - walletUsed) : 0;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await API.post("/orders", { ...f, outlet: outlet?.id || "", wallet_redeem: walletUsed, creator_ref: localStorage.getItem("suvikka_creator_ref") || "" }); setDone(data); refresh(); toast.success("Order placed"); localStorage.removeItem("suvikka_creator_ref"); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (done)
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="order-success">
        <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
        <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-6">Order Placed!</h1>
        <p className="text-stone-500 mt-3">Thank you for shopping with Suvikka. Your order is confirmed for Cash on Delivery.</p>
        <div className="mt-6 inline-block bg-white border border-stone-200 rounded-xl px-8 py-5">
          <p className="text-xs tracking-[0.2em] uppercase text-stone-400">Order ID</p>
          <p className="font-display text-2xl font-bold text-[#6A1B29] mt-1" data-testid="order-id">{done.order_id}</p>
          <p className="text-sm text-stone-600 mt-2">Total: <strong>{inr(done.total)}</strong></p>
        </div>
        <div className="mt-8 flex justify-center gap-3">
          <button onClick={() => nav("/style-hub")} data-testid="order-view-orders" className="bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full">View My Orders</button>
          <button onClick={() => nav("/products")} data-testid="order-continue" className="border border-stone-300 font-semibold px-6 py-3 rounded-full">Continue Shopping</button>
        </div>
      </div>
    );

  if (!cart || cart.count === 0)
    return <div className="max-w-2xl mx-auto px-4 py-24 text-center text-stone-500" data-testid="checkout-empty">Your bag is empty. <Link to="/products" className="text-[#6A1B29] font-medium">Shop now</Link></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="checkout-page">
      <h1 className="font-display text-3xl sm:text-4xl font-semibold">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-[#EADBD8] rounded-2xl p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 shadow-soft" data-testid="checkout-form">
          <h2 className="sm:col-span-2 font-display text-xl font-semibold">Shipping Details</h2>
          <input required placeholder="Full Name *" value={f.name} onChange={set("name")} className={F} data-testid="co-name" />
          <input required placeholder="Mobile *" value={f.mobile} onChange={set("mobile")} className={F} data-testid="co-mobile" />
          <textarea required placeholder="Address *" rows={2} value={f.address} onChange={set("address")} className={`${F} sm:col-span-2`} data-testid="co-address" />
          <input required placeholder="City *" value={f.city} onChange={set("city")} className={F} data-testid="co-city" />
          <select required value={f.state} onChange={set("state")} className={F} data-testid="co-state">
            <option value="">State *</option>{STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <input required placeholder="Pincode *" value={f.pincode} onChange={set("pincode")} className={F} data-testid="co-pincode" />
          <select value={f.payment_method} onChange={set("payment_method")} className={F} data-testid="co-payment">
            <option value="COD">Cash on Delivery</option>
          </select>
          <textarea placeholder="Order notes (optional)" rows={2} value={f.notes} onChange={set("notes")} className={`${F} sm:col-span-2`} data-testid="co-notes" />
          {wallet > 0 && (
            <label className="sm:col-span-2 flex items-center gap-3 bg-[#FFF5F8] border border-[#6A1B29]/20 rounded-lg px-4 py-3 cursor-pointer" data-testid="co-wallet-row">
              <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} className="accent-[#6A1B29]" data-testid="co-wallet-toggle" />
              <span className="text-sm text-stone-700">Use Suvikka Wallet balance <strong>{inr(wallet)}</strong> — up to {inr(walletCap)} off this order (max 10%)</span>
            </label>
          )}
          <button disabled={busy} className="sm:col-span-2 bg-[#6A1B29] hover:bg-[#501325] disabled:opacity-60 text-white font-semibold py-4 rounded-full" data-testid="place-order-button">
            {busy ? "Placing order…" : `PLACE ORDER · ${inr(payable)}`}
          </button>
        </form>
        <div className="bg-white border border-[#EADBD8] rounded-2xl p-6 h-fit shadow-soft" data-testid="checkout-summary">
          <h2 className="font-display text-xl font-semibold mb-4">Your Bag ({cart.count})</h2>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {cart.items.map((it) => (
              <div key={it.id} className="flex gap-3 text-sm">
                <img src={it.image} alt="" className="w-12 h-14 object-cover rounded" />
                <div className="flex-1"><p className="line-clamp-1">{it.title}</p><p className="text-stone-400">Qty {it.qty}</p></div>
                <p className="font-medium">{inr(it.line_total)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-200 mt-4 pt-3 text-sm">
            <div className="flex justify-between py-1"><span>Subtotal</span><span>{inr(cart.subtotal)}</span></div>
            {cart.discount > 0 && <div className="flex justify-between py-1 text-green-700"><span>Bundle offer</span><span>-{inr(cart.discount)}</span></div>}
            {walletUsed > 0 && <div className="flex justify-between py-1 text-green-700" data-testid="co-wallet-applied"><span>Suvikka Wallet</span><span>-{inr(walletUsed)}</span></div>}
            <div className="flex justify-between font-semibold text-lg mt-2"><span>Total</span><span>{inr(payable)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
